import { AskRequest } from '@hone-automation/common/lib/AskRequests';
import React, { createContext, useContext, useEffect, useReducer } from 'react';
import {
  filterAndSplitRequests,
  FilterProps,
  filterRequests,
  SplitedRequests,
  PrioritySplittedRequests,
  splitRequestsByPriority,
} from './utils/filterAndSearchUtils';
import selectElement, { selectAskRequestElement } from './utils/selectElement';
import { AskRequestStatus } from '@hone-automation/common';

/**
 * Default filter settings for request handling
 */
const defaultFilters: FilterProps = {
  status: 'Open',
  search: '',
  sort: 'oldest',
  groupBy: 'date',
};

// Define the initial state as a function that reads from URL search params
const initialState = (props: { meId?: string; requests?: AskRequest[] }) => {
  let status = defaultFilters.status;
  let search = defaultFilters.search;
  let sort = defaultFilters.sort;
  let groupBy = defaultFilters.groupBy;

  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    status = (searchParams.get('status') || defaultFilters.status) as FilterProps['status'];
    search = searchParams.get('search') || defaultFilters.search;
    sort = (searchParams.get('sort') || defaultFilters.sort) as FilterProps['sort'];
    groupBy = (searchParams.get('groupBy') || defaultFilters.groupBy) as FilterProps['groupBy'];
  }

  const initialFilters = {
    status,
    search,
    sort,
    groupBy,
  };

  const initialSplitRequests =
    props.requests && props.meId
      ? // TODO: bring back when assignment API is available
        // ? filterAndSplitRequests(props.requests, props.meId, initialFilters)
        // : ({ me: [], team: [] } as SplitedRequests);
        filterRequests(props.requests, initialFilters)
      : ([] as AskRequest[]);

  // Split requests by priority for initial state
  const initialPriorityGroups = splitRequestsByPriority(initialSplitRequests);

  return {
    requests: props.requests || [],
    splitRequests: initialSplitRequests,
    priorityGroups: initialPriorityGroups,
    // TODO: bring back when assignment API is available
    // selectedItem: initialSplitRequests && selectElement(initialSplitRequests, initialFilters.sort),
    selectedItem: initialSplitRequests && selectAskRequestElement(initialSplitRequests, initialFilters.sort),
    meId: props.meId || '',
    filters: initialFilters as FilterProps,
    collapsedGroups: {} as Record<string, boolean>,
    newRequestAdded: null as AskRequest | null,
  };
};

type SetRequestsAction = { type: 'SET_REQUESTS'; payload: AskRequest[] };
type SetSelectedItemAction = { type: 'SET_SELECTED_ITEM'; payload: AskRequest | null };
type SetMeIdAction = { type: 'SET_ME_ID'; payload: string };
type SetUpdatedFiltersAction = { type: 'SET_FILTERS'; payload: FilterProps };
type SetGroupCollapsedAction = { type: 'SET_GROUP_COLLAPSED'; payload: { groupId: string; isCollapsed: boolean } };
type AddNewRequestAction = { type: 'ADD_NEW_REQUEST'; payload: { request: AskRequest; status: AskRequestStatus } };
type SetNewRequestAddedAction = { type: 'SET_NEW_REQUEST_ADDED'; payload: AskRequest | null };
type Action =
  | SetRequestsAction
  | SetSelectedItemAction
  | SetMeIdAction
  | SetUpdatedFiltersAction
  | SetGroupCollapsedAction
  | AddNewRequestAction
  | SetNewRequestAddedAction;

const queueReducer = (state: ReturnType<typeof initialState>, action: Action) => {
  switch (action.type) {
    case 'SET_REQUESTS': {
      // TODO: bring back when assignment API is available
      // const splitRequests = filterAndSplitRequests(action.payload, state.meId, state.filters);
      const splitRequests = filterRequests(action.payload, state.filters);

      // Always split by priority when requests are updated
      const priorityGroups = splitRequestsByPriority(splitRequests);

      return {
        ...state,
        requests: action.payload,
        splitRequests: splitRequests,
        priorityGroups: priorityGroups,
        // selectedItem: !state.selectedItem ? splitRequests?.me[0] : state.selectedItem,
        selectedItem: !state.selectedItem ? splitRequests?.[0] : state.selectedItem,
      };
    }
    case 'SET_SELECTED_ITEM':
      return { ...state, selectedItem: action.payload };
    case 'SET_ME_ID':
      return { ...state, meId: action.payload };
    case 'SET_FILTERS': {
      // TODO: bring back when assignment API is available
      // const splitRequests = filterAndSplitRequests(state.requests, state.meId, action.payload);
      const splitRequests = filterRequests(state.requests, action.payload);

      // Update priority groups when filters change
      const priorityGroups = splitRequestsByPriority(splitRequests);

      return {
        ...state,
        filters: action.payload,
        splitRequests,
        priorityGroups,
        selectedItem: splitRequests && selectAskRequestElement(splitRequests, action.payload.sort),
      };
    }
    case 'SET_NEW_REQUEST_ADDED':
      return { ...state, newRequestAdded: action.payload };

    case 'SET_GROUP_COLLAPSED': {
      const { groupId, isCollapsed } = action.payload;
      return {
        ...state,
        collapsedGroups: {
          ...state.collapsedGroups,
          [groupId]: isCollapsed,
        },
      };
    }
    case 'ADD_NEW_REQUEST': {
      const { request, status } = action.payload;
      const newRequests = [...state.requests, request];
      const newFilters = { ...state.filters, status: status };
      const splitRequests = filterRequests(newRequests, newFilters);

      // Update priority groups when adding a new request
      const priorityGroups = splitRequestsByPriority(splitRequests);

      // Determine which group the new request belongs to
      const date = new Date(request.createdAt);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);

      let groupId = '';

      if (date.toDateString() === today.toDateString()) {
        groupId = 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        groupId = 'Yesterday';
      } else if (date >= new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)) {
        groupId = 'Last Week';
      } else if (date >= new Date(today.getFullYear(), today.getMonth(), 1)) {
        groupId = 'This Month';
      } else if (date >= new Date(today.getFullYear(), today.getMonth() - 1, 1)) {
        groupId = 'Last Month';
      } else if (date.getFullYear() === today.getFullYear()) {
        groupId = `${date.toLocaleString('default', { month: 'long' })} ${today.getFullYear().toString().slice(-2)}`;
      } else {
        groupId = date.getFullYear().toString();
      }

      // Update collapsedGroups to ensure the group containing the new request is expanded
      const updatedCollapsedGroups = { ...state.collapsedGroups };
      if (groupId) {
        updatedCollapsedGroups[groupId] = false;
      }

      return {
        ...state,
        requests: newRequests,
        filters: newFilters,
        splitRequests,
        priorityGroups,
        selectedItem: request,
        collapsedGroups: updatedCollapsedGroups,
        newRequestAdded: request, // Set the newly added request
      };
    }
    default:
      return state;
  }
};

const QueueContext = createContext<{
  state: ReturnType<typeof initialState>;
  dispatch: React.Dispatch<Action>;
}>({
  state: initialState({}),
  dispatch: () => {},
});

/**
 * Hook to access the Queue context data and actions
 * @returns {Object} Queue context value containing state and dispatch function
 */
export const useQueueContext = () => useContext(QueueContext);

/**
 * Provider component for Queue context
 * Manages state for requests filtering, selection, and synchronization with URL parameters
 *
 * @param {Object} props - The provider props
 * @param {string} [props.meId] - Current user ID for filtering requests
 * @param {AskRequest[]} [props.requests] - Array of request data
 * @param {React.ReactNode} props.children - Child components that will have access to the context
 * @returns {JSX.Element} The provider component
 */
export const QueueContextProvider = ({
  meId,
  requests,
  children,
}: {
  meId?: string;
  requests?: AskRequest[];
  children: React.ReactNode;
}) => {
  // @ts-ignore
  const [state, dispatch] = useReducer(queueReducer, initialState({ meId, requests }));

  // Sync state to URL parameters
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const newParams = new URLSearchParams(window.location.search);

    (Object.entries(state.filters) as [keyof FilterProps, string][]).forEach(([key, value]) => {
      // Use toString() only if value is not already a string
      newParams.set(key, value);
    });

    const newUrl = `${window.location.pathname}?${newParams.toString()}`;
    window.history.replaceState(null, '', newUrl);
  }, [state.filters]);

  return <QueueContext.Provider value={{ state, dispatch }}>{children}</QueueContext.Provider>;
};
